import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import LZString from 'lz-string';
import { applyDisplayTransforms, wrapAngle } from '../utils/geometry.js';
import { readLayout } from '../utils/orientationMigration.js';
import { DEFAULT_FABRIC_CODE } from '../utils/constants.js';

// Isometric-ish three-quarter view of a single element, from front-right.
const ELEMENT_VIEW_DIR = new THREE.Vector3(0.7, 0.5, 0.7);
// Layouts are framed wider and seen from the front-left.
const LAYOUT_VIEW_DIR = new THREE.Vector3(-0.8, 0.6, 0.3);
const LAYOUT_FRAME_SCALE = 1.4;

/**
 * ThumbnailRenderer - Generates thumbnails of furniture elements
 * Uses off-screen rendering to avoid impacting main render loop performance
 */
export class ThumbnailRenderer {
  constructor(renderer) {
    // Kept so thumbnails can mirror the live scene's tone mapping / color space.
    this.mainRenderer = renderer;
    this.materialManager = null;

    // Configuration
    this.size = 256; // Thumbnail resolution
    this.quality = 0.85; // WebP encode quality
    this.padding = 1.6; // Camera padding factor around object (higher = more zoomed out)

    // Cache for generated thumbnails
    this.thumbnailCache = new Map();

    // Serial queue for layout thumbnails — prevents concurrent renders from
    // overwriting the shared this.fabricMaterial before cloneForLayout uses it
    this._renderQueue = Promise.resolve();

    // Cached fabric data for ET-14
    this.defaultFabricData = null;

    // Setup off-screen rendering
    this.setupRenderer();
    this.setupScene();
    this.setupCamera();
    this.setupEnvironment();
    this.setupMaterials();
  }

  /**
   * Set the MaterialManager reference for textured thumbnails
   * @param {MaterialManager} materialManager
   */
  setMaterialManager(materialManager) {
    this.materialManager = materialManager;
  }

  /**
   * Set the FurnitureManager reference for layout thumbnails
   * @param {FurnitureManager} furnitureManager
   */
  setFurnitureManager(furnitureManager) {
    this.furnitureManager = furnitureManager;
  }

  // Dedicated offscreen renderer/canvas so we read pixels via canvas.toDataURL()
  // (WebP) — no readRenderTargetPixels stall, no manual flip, no PNG encode.
  setupRenderer() {
    this._canvas = document.createElement('canvas');
    this.renderer = new THREE.WebGLRenderer({
      canvas: this._canvas,
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true, // keeps toDataURL() valid regardless of compositor timing
    });
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(this.size, this.size, false);
    this.renderer.setClearColor(0x000000, 0); // Transparent background
    this._lastSize = this.size;
  }

  /**
   * Setup dedicated scene for thumbnail rendering
   */
  setupScene() {
    this.scene = new THREE.Scene();
    this.scene.background = null; // Transparent background

    // Low direct lights for the ACES/sRGB pipeline; IBL (setupEnvironment) does the fill.
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
    this.scene.add(ambientLight);

    // Key light for form and shading.
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1.8);
    directionalLight.position.set(2, 3, 2);
    this.scene.add(directionalLight);

    // Opposite fill.
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
    fillLight.position.set(-1, 1, -1);
    this.scene.add(fillLight);
  }

  /**
   * Setup environment map for PBR materials
   */
  setupEnvironment() {
    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    pmremGenerator.compileEquirectangularShader();

    // Use RoomEnvironment for neutral studio-like lighting
    const roomEnv = new RoomEnvironment();
    this.envMap = pmremGenerator.fromScene(roomEnv).texture;

    pmremGenerator.dispose();
    roomEnv.dispose();

    // Apply IBL for soft fill; RoomEnvironment is very bright, so scale it well down.
    this.scene.environment = this.envMap;
    this.scene.environmentIntensity = 0.3;
  }

  /**
   * Setup orthographic camera for consistent thumbnail framing
   */
  setupCamera() {
    // Will be adjusted per-object based on bounding box
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
    this.camera.position.set(1, 1, 1); // Isometric-ish view
    this.camera.lookAt(0, 0, 0);
  }

  /**
   * Setup reusable materials and temporary vectors
   */
  setupMaterials() {
    // Fallback material - neutral fabric color
    this.fallbackMaterial = new THREE.MeshStandardMaterial({
      color: 0x9a8b7a,
      roughness: 0.8,
      metalness: 0.0,
    });

    // Wood/legs material
    this.woodMaterial = new THREE.MeshStandardMaterial({
      color: 0x8B7355,
      roughness: 0.7,
      metalness: 0.0,
    });

    // Metal material
    this.metalMaterial = new THREE.MeshStandardMaterial({
      color: 0x888888,
      roughness: 0.3,
      metalness: 0.8,
    });

    // Plastic material
    this.plasticMaterial = new THREE.MeshStandardMaterial({
      color: 0x333333,
      roughness: 0.4,
      metalness: 0.1,
    });

    // Fabric material (will be updated with texture when available)
    this.fabricMaterial = null;

    // Reusable temporary vectors/quaternions to reduce GC pressure
    this._tempWorldPos = new THREE.Vector3();
    this._tempWorldQuat = new THREE.Quaternion();
    this._tempWorldScale = new THREE.Vector3();
    this._tempRootPos = new THREE.Vector3();

    // Reusable objects for camera fitting
    this._tempBox = new THREE.Box3();
    this._tempSize = new THREE.Vector3();
    this._tempCenter = new THREE.Vector3();
  }

  /**
   * Load the default ET-14 fabric data
   */
  async loadDefaultFabric() {
    if (this.defaultFabricData) return this.defaultFabricData;

    // Deduplicate concurrent calls — return the same in-flight promise.
    if (this._defaultFabricPromise) return this._defaultFabricPromise;

    if (!this.materialManager) {
      console.warn('ThumbnailRenderer: MaterialManager not set, using fallback material');
      return null;
    }

    this._defaultFabricPromise = (async () => {
      try {
        const fabricId = this.materialManager.getFabricIdByCode(DEFAULT_FABRIC_CODE);
        if (!fabricId) {
          console.warn(`ThumbnailRenderer: ${DEFAULT_FABRIC_CODE} fabric not found`);
          return null;
        }
        this.defaultFabricData = await this.materialManager.getFabricData(fabricId);
        return this.defaultFabricData;
      } catch (error) {
        console.warn(`ThumbnailRenderer: Failed to load ${DEFAULT_FABRIC_CODE} fabric:`, error);
        return null;
      } finally {
        this._defaultFabricPromise = null;
      }
    })();

    return this._defaultFabricPromise;
  }

  _createFabricMaterial(fabricData) {
    return new THREE.MeshStandardMaterial({
      map: fabricData.texture || null,
      color: 0xffffff,
      roughness: 0.8,
      metalness: 0.0,
    });
  }

  // Renders share this.fabricMaterial between loading a fabric and cloning with it, so they
  // run one at a time, in call order.
  async _enqueue(render) {
    let release;
    const slot = new Promise(r => { release = r; });
    const previous = this._renderQueue;
    this._renderQueue = slot;
    await previous;
    try {
      return await render();
    } finally {
      release();
    }
  }

  /**
   * Load a specific fabric by code and set it as the active fabric material
   */
  async loadFabricByCode(fabricCode) {
    if (!this.materialManager) return null;

    try {
      const fabricId = this.materialManager.getFabricIdByCode(fabricCode);
      if (!fabricId) {
        // Fall back to default
        return this.loadDefaultFabric();
      }

      const fabricData = await this.materialManager.getFabricData(fabricId);
      if (!fabricData) {
        return this.loadDefaultFabric();
      }

      // Dispose previous fabric material if it exists
      if (this.fabricMaterial) {
        this.fabricMaterial.dispose();
        this.fabricMaterial = null;
      }

      this.fabricMaterial = this._createFabricMaterial(fabricData);

      return fabricData;
    } catch (error) {
      console.warn(`ThumbnailRenderer: Failed to load fabric ${fabricCode}:`, error);
      return this.loadDefaultFabric();
    }
  }

  /**
   * Generate thumbnail from raw mesh (for use without FurnitureElement)
   * Serialized through _enqueue so concurrent calls don't race on this.fabricMaterial.
   * @param {THREE.Object3D} mesh - The mesh to render
   * @param {string} cacheKey - Optional cache key
   * @param {Object} options - Rendering options
   * @returns {Promise<string>} - Data URL of the thumbnail image
   */
  async generateThumbnailFromMesh(mesh, cacheKey = null, options = {}) {
    // Fast-path: if cached, skip the queue entirely.
    const { useCache = true } = options;
    if (useCache && cacheKey && this.thumbnailCache.has(cacheKey)) {
      return this.thumbnailCache.get(cacheKey);
    }

    return this._enqueue(() => this._doGenerateThumbnailFromMesh(mesh, cacheKey, options));
  }

  async _doGenerateThumbnailFromMesh(mesh, cacheKey = null, options = {}) {
    const {
      useCache = true,
      size = this.size,
      fabricCode = null,
    } = options;

    // Check cache (may now be populated by a prior queued render)
    if (useCache && cacheKey && this.thumbnailCache.has(cacheKey)) {
      return this.thumbnailCache.get(cacheKey);
    }

    if (fabricCode) {
      await this.loadFabricByCode(fabricCode);
    } else {
      await this.loadDefaultFabric();
    }

    // Create a clean clone for rendering
    const thumbnailMesh = await this.prepareClone(mesh);

    // Add to scene
    this.scene.add(thumbnailMesh);

    // Adjust camera to fit the object
    this.fitCameraToObject(thumbnailMesh);

    // Render to target
    const dataUrl = this.render(size);

    // Cleanup
    this.scene.remove(thumbnailMesh);
    this.disposeClone(thumbnailMesh);

    // Reset fabric material if a custom fabric was used, so it doesn't leak into other renders
    if (fabricCode && this.fabricMaterial) {
      this.fabricMaterial.dispose();
      this.fabricMaterial = null;
    }

    // Cache the result
    if (useCache && cacheKey) {
      this.thumbnailCache.set(cacheKey, dataUrl);
    }

    return dataUrl;
  }

  /**
   * Prepare a clone of the mesh with appropriate materials for thumbnail
   * @param {THREE.Object3D} originalMesh - The original mesh
   * @param {Object} options - Options for cloning
   * @param {boolean} options.centerAtOrigin - Whether to center the clone at origin (default: true)
   * @returns {THREE.Group} - The prepared clone
   */
  async prepareClone(originalMesh, options = {}) {
    const { centerAtOrigin = true } = options;
    const group = new THREE.Group();

    // Clone the mesh structure
    originalMesh.traverse((child) => {
      if (child.isMesh && child.geometry) {
        // prepareClone flattens the hierarchy (each isMesh is reparented under
        // `group`), so parent-level `visible: false` is lost. Walk up the
        // chain — stop at originalMesh — and skip the mesh if any ancestor
        // inside this subtree is hidden. This is what makes variant
        // `nodeAnimations: [{ visible: false }]` actually hide nodes
        // (e.g. clooods-co "Corner M L/R" hiding corner_back_support / pillow).
        let ancestor = child;
        while (ancestor) {
          if (ancestor.visible === false) return;
          if (ancestor === originalMesh) break;
          ancestor = ancestor.parent;
        }

        const material = this.getMaterialForMesh(child);

        // Share source geometry (no clone); the clone owns none of it.
        const clonedMesh = new THREE.Mesh(child.geometry, material);
        this.copyTransform(child, clonedMesh, originalMesh);
        group.add(clonedMesh);
      }
    });

    // Center the group at origin (unless disabled for layout rendering)
    if (centerAtOrigin) {
      this._tempBox.setFromObject(group);
      this._tempBox.getCenter(this._tempCenter);
      group.position.sub(this._tempCenter);
    }

    return group;
  }

  /**
   * Get the appropriate material for a mesh based on its name
   * Uses cached MeshStandardMaterials for better performance
   * @param {THREE.Mesh} mesh
   * @returns {THREE.Material}
   */
  getMaterialForMesh(mesh) {
    const meshName = (mesh.name || '').toLowerCase();
    const materialName = (mesh.material?.name || '').toLowerCase();

    // Check if this is a wood/leg material
    if (meshName.includes('leg') || meshName.includes('wood') || meshName.includes('frame') ||
        materialName.includes('wood') || materialName.includes('leg')) {
      return this.woodMaterial;
    }

    // Check if this is metal
    if (meshName.includes('metal') || materialName.includes('metal')) {
      return this.metalMaterial;
    }

    // Check if this is plastic
    if (meshName.includes('plastic') || materialName.includes('plastic')) {
      return this.plasticMaterial;
    }

    // Default to fabric material
    if (this.fabricMaterial) {
      return this.fabricMaterial;
    }
    if (this.defaultFabricData) {
      this.fabricMaterial = this._createFabricMaterial(this.defaultFabricData);
      return this.fabricMaterial;
    }

    // Fallback - neutral fabric color
    return this.fallbackMaterial;
  }

  /**
   * Copy world transform from source to target, relative to root
   * Uses reusable vectors to reduce garbage collection
   */
  copyTransform(source, target, root) {
    // Get world position/rotation/scale relative to root (reusing temp vectors)
    source.getWorldPosition(this._tempWorldPos);
    source.getWorldQuaternion(this._tempWorldQuat);
    source.getWorldScale(this._tempWorldScale);

    // Subtract root position to get relative position
    root.getWorldPosition(this._tempRootPos);
    this._tempWorldPos.sub(this._tempRootPos);

    target.position.copy(this._tempWorldPos);
    target.quaternion.copy(this._tempWorldQuat);
    target.scale.copy(this._tempWorldScale);
  }

  /**
   * Fit camera to show the entire object
   * @param {THREE.Object3D} object - The object to frame
   */
  fitCameraToObject(object) {
    this._fitOrthoCamera(object, this.padding, 1, ELEMENT_VIEW_DIR);
  }

  /**
   * Frame `object` in the orthographic camera, looking at its centre from `viewDir`.
   * The frustum spans maxDim * frameScale; reachScale scales the camera distance and far plane.
   */
  _fitOrthoCamera(object, frameScale, reachScale, viewDir) {
    this._tempBox.setFromObject(object);
    const size = this._tempBox.getSize(this._tempSize);
    const center = this._tempBox.getCenter(this._tempCenter);

    const maxDim = Math.max(size.x, size.y, size.z);
    const halfSize = maxDim * frameScale * 0.5;
    const reach = maxDim * reachScale;

    this.camera.left = -halfSize;
    this.camera.right = halfSize;
    this.camera.top = halfSize;
    this.camera.bottom = -halfSize;
    this.camera.near = 0.1;
    this.camera.far = reach * 10;

    this.camera.position.copy(center).addScaledVector(viewDir, reach * 2);
    this.camera.lookAt(center);
    this.camera.updateProjectionMatrix();
  }

  /**
   * Render to the render target and return data URL
   * @param {number} size - Output size
   * @returns {string} - Data URL
   */
  render(size = this.size) {
    // Resize the offscreen canvas only when the requested size changes.
    if (this._lastSize !== size) {
      this.renderer.setSize(size, size, false);
      this._lastSize = size;
    }

    // Mirror the live scene's output so thumbnails look like the real product.
    if (this.mainRenderer) {
      this.renderer.toneMapping = this.mainRenderer.toneMapping;
      this.renderer.toneMappingExposure = this.mainRenderer.toneMappingExposure;
      this.renderer.outputColorSpace = this.mainRenderer.outputColorSpace;
    }

    this.renderer.render(this.scene, this.camera);

    // Read straight off the canvas (Safari lacks WebP encoding, falls back to PNG).
    return this.renderer.domElement.toDataURL('image/webp', this.quality);
  }

  /**
   * Dispose of cloned meshes and their resources
   * Geometry is borrowed from the source mesh, so only non-cached materials are freed
   * @param {THREE.Object3D} clone
   */
  disposeClone(clone) {
    const cachedMaterials = [
      this.fallbackMaterial,
      this.woodMaterial,
      this.metalMaterial,
      this.plasticMaterial,
      this.fabricMaterial,
    ];

    clone.traverse((child) => {
      // Only dispose materials that aren't cached
      if (child.material && !cachedMaterials.includes(child.material)) {
        if (Array.isArray(child.material)) {
          child.material.forEach(mat => {
            if (!cachedMaterials.includes(mat)) mat.dispose();
          });
        } else {
          child.material.dispose();
        }
      }
    });
  }

  /**
   * Generate thumbnail for an entire layout from compressed data
   * @param {string} compressedData - LZString compressed layout data
   * @param {string} cacheKey - Cache key for the thumbnail
   * @param {Object} options - Rendering options
   * @returns {Promise<string|null>} - Data URL of the thumbnail image
   */
  async generateLayoutThumbnail(compressedData, cacheKey, options = {}) {
    return this._enqueue(() => this._doGenerateLayoutThumbnail(compressedData, cacheKey, options));
  }

  async _doGenerateLayoutThumbnail(compressedData, cacheKey, options = {}) {
    const {
      useCache = true,
      size = this.size,
      fabricCode = null,
    } = options;

    // Check cache first
    if (useCache && cacheKey && this.thumbnailCache.has(cacheKey)) {
      return this.thumbnailCache.get(cacheKey);
    }

    if (!this.furnitureManager) {
      console.warn('ThumbnailRenderer: FurnitureManager not set');
      return null;
    }

    try {
      // Decompress the layout data
      const decompressed = LZString.decompressFromEncodedURIComponent(compressedData);
      if (!decompressed) {
        console.warn('ThumbnailRenderer: Failed to decompress layout data');
        return null;
      }

      const state = JSON.parse(decompressed);
      const furnitureItems = readLayout(state, (item) => this.furnitureManager.findCatalogItem(item)).items;

      if (furnitureItems.length === 0) {
        return null;
      }

      // Load fabric for textured rendering - use provided fabricCode or fall back to default
      if (fabricCode) {
        await this.loadFabricByCode(fabricCode);
      } else {
        await this.loadDefaultFabric();
      }

      // Create a group to hold all furniture
      // The layout stage (view dir, lights, IBL) still faces -X, so turn the layout to meet it.
      const layoutGroup = new THREE.Group();
      layoutGroup.rotation.y = -Math.PI / 2;
      const clonesToDispose = [];

      // Build maps for parent lookups and tracking positioned items
      const itemById = new Map();
      furnitureItems.forEach(item => itemById.set(item.id, item));
      const positionedClones = new Map(); // id -> { clone, worldPos, worldRotation }

      // Separate items into:
      // 1. Root items (no parent) - world positions
      // 2. Module-to-module connections - world positions
      // 3. Component attachments - local positions needing transformation
      const worldPositionItems = [];
      const localPositionItems = [];

      for (const item of furnitureItems) {
        if (!item.attachedTo) {
          // No parent - root item with world position
          worldPositionItems.push(item);
        } else {
          const parentItem = itemById.get(item.attachedTo);
          if (!parentItem) {
            // Parent not in list - treat as root
            worldPositionItems.push(item);
          } else {
            // Check if this is module-to-module connection
            const isModuleToModule =
              item.componentType === 'module' && parentItem.componentType === 'module';
            if (isModuleToModule) {
              // Module-to-module: world position
              worldPositionItems.push(item);
            } else {
              // Component attachment: local position
              localPositionItems.push(item);
            }
          }
        }
      }

      // First pass: position items with world coordinates
      for (const item of worldPositionItems) {
        try {
          const furnitureData = this.findFurnitureByUrlCode(item.urlCode);
          if (!furnitureData || !furnitureData.model) continue;

          const modelEntry = await this.furnitureManager.getOrLoadModel(furnitureData.model);
          const clone = this.cloneForLayout(modelEntry.mesh);
          applyDisplayTransforms(clone, furnitureData, item.variantIndex ?? 0);

          // Position at stored world position
          if (item.position) {
            clone.position.set(item.position.x, item.position.y, item.position.z);
          }
          if (item.rotation !== undefined) {
            clone.rotation.y = item.rotation;
          }

          layoutGroup.add(clone);
          clonesToDispose.push(clone);

          // Force matrix update so children can be properly positioned
          clone.updateMatrix();
          clone.updateMatrixWorld(true);

          positionedClones.set(item.id, {
            clone,
            worldPos: clone.position.clone(),
            worldRotation: item.rotation || 0
          });
        } catch (error) {
          console.warn(`ThumbnailRenderer: Failed to load furniture ${item.urlCode}:`, error);
        }
      }

      // Update layoutGroup matrix before second pass
      layoutGroup.updateMatrixWorld(true);

      // Second pass: position component attachments using scene graph parenting
      // Add components as children of their parents and let Three.js handle transforms
      let remainingItems = [...localPositionItems];
      let maxPasses = 5;

      while (remainingItems.length > 0 && maxPasses > 0) {
        const stillRemaining = [];

        for (const item of remainingItems) {
          const parentData = positionedClones.get(item.attachedTo);
          if (!parentData) {
            stillRemaining.push(item);
            continue;
          }

          try {
            const furnitureData = this.findFurnitureByUrlCode(item.urlCode);
            if (!furnitureData || !furnitureData.model) continue;

            const modelEntry = await this.furnitureManager.getOrLoadModel(furnitureData.model);
            const clone = this.cloneForLayout(modelEntry.mesh);
            applyDisplayTransforms(clone, furnitureData, item.variantIndex ?? 0);

            // Add as child of parent - position and rotation are local
            parentData.clone.add(clone);

            // Set stored local position and rotation
            if (item.position) {
              clone.position.set(item.position.x, item.position.y, item.position.z);
            }
            clone.rotation.y = item.rotation || 0;

            // For inherit side backrests (cascais-n*) on a panel facing the room, adjust orientation
            const isBackrest = item.urlCode && item.urlCode.match(/cascais-n[a-z]/);
            if (item.attachedSide === 'inherit' && isBackrest && Math.abs(wrapAngle(parentData.worldRotation - Math.PI)) < 0.1) {
              clone.position.z = -clone.position.z;
              clone.rotation.y += Math.PI;
            }

            clonesToDispose.push(clone);

            clone.updateMatrixWorld(true);
            const worldPos = new THREE.Vector3();
            clone.getWorldPosition(worldPos);
            positionedClones.set(item.id, {
              clone,
              worldPos: worldPos,
              worldRotation: parentData.worldRotation + (item.rotation || 0)
            });
          } catch (error) {
            console.warn(`ThumbnailRenderer: Failed to load furniture ${item.urlCode}:`, error);
          }
        }

        remainingItems = stillRemaining;
        maxPasses--;
      }

      if (layoutGroup.children.length === 0) {
        return null;
      }

      // Add to scene
      this.scene.add(layoutGroup);

      // Fit camera to show entire layout from above
      this.fitCameraToLayout(layoutGroup);

      // Render
      const dataUrl = this.render(size);

      // Cleanup
      this.scene.remove(layoutGroup);
      for (const clone of clonesToDispose) {
        this.disposeClone(clone);
      }

      // Reset fabric material if a custom fabric was used so it doesn't leak into other renders
      if (fabricCode && this.fabricMaterial) {
        this.fabricMaterial.dispose();
        this.fabricMaterial = null;
      }

      // Cache the result
      if (useCache && cacheKey) {
        this.thumbnailCache.set(cacheKey, dataUrl);
      }

      return dataUrl;
    } catch (error) {
      console.warn('ThumbnailRenderer: Failed to generate layout thumbnail:', error);
      return null;
    }
  }

  /**
   * Simple clone for layout thumbnails - preserves hierarchy and transforms
   * @param {THREE.Object3D} original - The original model
   * @returns {THREE.Object3D} - Cloned model with thumbnail materials
   */
  cloneForLayout(original) {
    // Deep clone preserving hierarchy
    const clone = original.clone(true);

    // Reset transforms for consistent positioning
    clone.position.set(0, 0, 0);
    clone.rotation.set(0, 0, 0);
    clone.scale.set(1, 1, 1);

    // Apply thumbnail materials; geometry stays shared with the source model.
    clone.traverse((child) => {
      if (child.isMesh) {
        child.material = this.getMaterialForMesh(child);
      }
    });

    return clone;
  }

  /**
   * Find furniture data by urlCode across all collections
   * @param {string} urlCode
   * @returns {Object|null}
   */
  findFurnitureByUrlCode(urlCode) {
    return this.furnitureManager?.findCatalogItemByUrlCode(urlCode) ?? null;
  }

  /**
   * Fit camera to show entire layout from a top-down isometric view
   * @param {THREE.Object3D} layoutGroup
   */
  fitCameraToLayout(layoutGroup) {
    this._fitOrthoCamera(layoutGroup, LAYOUT_FRAME_SCALE, LAYOUT_FRAME_SCALE, LAYOUT_VIEW_DIR);
  }

  /**
   * Dispose all resources
   */
  dispose() {
    this.renderer.dispose();
    this.renderer.forceContextLoss();
    this.fallbackMaterial.dispose();
    this.woodMaterial.dispose();
    this.metalMaterial.dispose();
    this.plasticMaterial.dispose();
    if (this.fabricMaterial) {
      this.fabricMaterial.dispose();
    }
    if (this.envMap) {
      this.envMap.dispose();
    }
    this.thumbnailCache.clear();
  }
}
